<template>
  <div>
    <div class="vx-col w-full mb-base">
      <vx-card class="w-full">
        <div style="margin-bottom:12px;">
          <router-link to="/youtube-cid" style="opacity:0.5;font-size:12px;text-decoration:none;" class="ytcid-back-link">← 소유권 주장</router-link>
        </div>

        <div v-if="loading" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">불러오는 중…</div>
        <div v-else-if="!claim" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">소유권 주장을 찾을 수 없습니다.</div>

        <template v-else>
          <div class="flex items-center justify-between mb-4" style="padding-bottom:16px;border-bottom:1px solid rgba(0,0,0,0.06);">
            <div style="display:flex;flex-direction:column;gap:8px;min-width:0;">
              <div style="display:flex;align-items:center;gap:12px;">
                <span style="display:inline-block;width:3px;height:18px;background:#7367F0;border-radius:2px;"></span>
                <IdMono :value="claim.id"></IdMono>
              </div>
              <div>
                <StatusPill :variant="statusVariant"></StatusPill>
              </div>
            </div>
            <div>
              <el-button-group v-if="claim.status === 'active'">
                <el-button
                  size="small"
                  icon="el-icon-refresh"
                  plain
                  @click="openPolicyDialog"
                >
                  정책 변경
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  icon="el-icon-circle-close"
                  plain
                  @click="releaseDialogOpen = true"
                >
                  소유권 주장 해제
                </el-button>
              </el-button-group>
            </div>
          </div>

          <div style="display:grid;grid-template-columns:2fr 1fr;gap:32px;margin-top:20px;">
            <section>
              <p style="font-size:10px;text-transform:uppercase;letter-spacing:0.8px;opacity:0.5;font-weight:600;margin:0 0 12px;">소유권 주장 정보</p>
              <dl style="display:grid;grid-template-columns:140px 1fr;gap:10px 24px;margin:0;font-size:13px;">
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">저작물</dt>
                <dd style="margin:0;">
                  <router-link v-if="claim.assetId" :to="`/youtube-cid/asset/${claim.assetId}`" style="text-decoration:none;color:inherit;" class="ytcid-link">
                    <IdMono :value="claim.assetId" :copyable="false"></IdMono>
                  </router-link>
                  <span v-else>—</span>
                  <span v-if="assetTitle" style="opacity:0.55;margin-left:6px;">· {{ assetTitle }}</span>
                </dd>
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">동영상</dt>
                <dd style="margin:0;">
                  <a v-if="claim.videoId" :href="`https://youtube.com/watch?v=${claim.videoId}`" target="_blank" rel="noopener" style="text-decoration:none;color:inherit;" class="ytcid-link">
                    <IdMono :value="claim.videoId" :copyable="false"></IdMono>
                  </a>
                </dd>
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">콘텐츠 유형</dt>
                <dd style="margin:0;">{{ claim.contentType || '—' }}</dd>
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">적용 정책</dt>
                <dd style="margin:0;">
                  <div v-if="appliedPolicyActions.length" style="display:inline-flex;flex-wrap:wrap;gap:4px;align-items:center;">
                    <StatusPill v-for="action in appliedPolicyActions" :key="action" :variant="action"></StatusPill>
                  </div>
                  <span v-else-if="appliedPolicyName">{{ appliedPolicyName }}</span>
                  <span v-else>—</span>
                  <div v-if="appliedPolicyActions.length && appliedPolicyName" style="font-size:11px;opacity:0.55;margin-top:2px;">{{ appliedPolicyName }}</div>
                </dd>
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">출처</dt>
                <dd style="margin:0;">{{ claim.origin && claim.origin.source || '—' }}</dd>
                <dt style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;">생성일시</dt>
                <dd style="margin:0;font-family:monospace;opacity:0.75;">{{ formatTime(claim.timeCreated) }}</dd>
              </dl>
            </section>

            <section>
              <p style="font-size:10px;text-transform:uppercase;letter-spacing:0.8px;opacity:0.5;font-weight:600;margin:0 0 12px;">기록</p>
              <div v-if="!history.length" style="opacity:0.4;font-size:12px;">기록이 없습니다.</div>
              <ol v-else style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;">
                <li v-for="(e, idx) in history" :key="idx" style="display:grid;grid-template-columns:140px 1fr;gap:12px;font-size:12px;">
                  <span style="opacity:0.55;font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;">{{ formatTime(e.time) }}</span>
                  <span>{{ e.event || e.type || '이벤트' }}</span>
                </li>
              </ol>
            </section>
          </div>
        </template>
      </vx-card>
    </div>

    <ConfirmActionDialog
      :visible.sync="releaseDialogOpen"
      title="소유권 주장 해제"
      :confirm-token="claim ? claim.id : ''"
      confirm-label="소유권 주장 해제"
      :dangerous="true"
      :submitting="submitting"
      @confirm="onRelease"
    >
      <template #subject v-if="claim">
        소유권 주장 <strong>{{ claim.id }}</strong> · 동영상 <strong>{{ claim.videoId }}</strong>
      </template>
      <template #diff v-if="claim">
        <div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:12px;line-height:1.6;">
          <div style="color:#EA5455;">− status: {{ claim.status }}</div>
          <div style="color:#28C76F;">+ status: inactive</div>
        </div>
      </template>
    </ConfirmActionDialog>

    <ConfirmActionDialog
      :visible.sync="policyDialogOpen"
      title="적용 정책 변경"
      :confirm-token="claim ? claim.id : ''"
      confirm-label="정책 적용"
      :dangerous="false"
      :submitting="submitting"
      :disabled="!selectedPolicyId"
      dialog-width="820px"
      @confirm="onChangePolicy"
    >
      <template #subject v-if="claim">
        소유권 주장 <strong>{{ claim.id }}</strong> · 동영상 <strong>{{ claim.videoId }}</strong>
      </template>
      <template #diff v-if="claim">
        <div class="policy-swap">
          <div class="policy-swap__col">
            <div class="policy-swap__label">변경 전</div>
            <div class="policy-swap__name">{{ currentPolicyName || '—' }}</div>
            <div v-if="loadingPolicies" class="policy-swap__loading">불러오는 중…</div>
            <PolicyRules v-else :rules="currentPolicyRules"></PolicyRules>
          </div>
          <div class="policy-swap__arrow" aria-hidden="true">→</div>
          <div class="policy-swap__col">
            <div class="policy-swap__label">변경 후</div>
            <div v-if="proposedPolicy" class="policy-swap__name">{{ proposedPolicy.name || proposedPolicy.id }}</div>
            <div v-else class="policy-swap__name policy-swap__name--placeholder">정책을 선택하세요</div>
            <PolicyRules v-if="proposedPolicy" :rules="proposedPolicy.rules || []"></PolicyRules>
          </div>
        </div>
        <div class="policy-swap__picker">
          <label class="policy-swap__picker-label">저장된 정책 선택</label>
          <el-select
            v-model="selectedPolicyId"
            placeholder="저장된 정책 선택"
            filterable
            style="width:100%;"
            @change="onPolicyChange"
          >
            <el-option v-for="p in policies" :key="p.id" :label="p.name || p.id" :value="p.id"></el-option>
          </el-select>
        </div>
      </template>
    </ConfirmActionDialog>
  </div>
</template>

<script>
import { YoutubeCidApi } from './api.js'
import IdMono from './components/IdMono.vue'
import StatusPill from './components/StatusPill.vue'
import ConfirmActionDialog from './components/ConfirmActionDialog.vue'
import PolicyRules from './components/PolicyRules.vue'
import { toLocalDateTime } from './dateFormat.js'

const STATUS_MAP = {
  active: 'active',
  inactive: 'inactive',
  pending: 'pending',
  takedown: 'takedown',
  appealed: 'appealed',
  disputed: 'disputed',
}

function ruleKey(r) {
  if (!r || typeof r !== 'object') return ''
  const action = (r.action || '').toLowerCase()
  const subaction = (r.subaction || '').toLowerCase()
  const terr = (r.conditions && r.conditions.requiredTerritories) || {}
  const type = terr.type || ''
  const territories = Array.isArray(terr.territories) ? [...terr.territories].sort().join(',') : ''
  return `${action}|${subaction}|${type}|${territories}`
}

function policyKey(rules) {
  return (rules || [])
    .filter((r) => {
      const t = r && r.conditions && r.conditions.requiredTerritories && r.conditions.requiredTerritories.type
      return t !== 'exclude'
    })
    .map(ruleKey)
    .sort()
    .join(';')
}

export default {
  name: 'YoutubeCidClaimDetail',
  components: { IdMono, StatusPill, ConfirmActionDialog, PolicyRules },
  data() {
    return {
      loading: false,
      claim: null,
      history: [],
      assetTitle: '',
      releaseDialogOpen: false,
      submitting: false,
      policyDialogOpen: false,
      policies: [],
      loadingPolicies: false,
      selectedPolicyId: '',
      proposedPolicy: null,
      currentPolicyRules: [],
      currentPolicyName: '',
    }
  },
  computed: {
    statusVariant() {
      return STATUS_MAP[(this.claim && this.claim.status || '').toLowerCase()] || 'inactive'
    },
    appliedPolicyName() {
      const p = this.claim && this.claim.appliedPolicy
      if (!p) return ''
      return p.name || p.id || ''
    },
    appliedPolicyActions() {
      const rules = this.claim && this.claim.appliedPolicy && this.claim.appliedPolicy.rules
      if (!Array.isArray(rules) || !rules.length) return []
      const seen = new Set()
      const out = []
      for (const r of rules) {
        const a = r && typeof r.action === 'string' ? r.action.toLowerCase() : ''
        if (!a || seen.has(a)) continue
        if (a !== 'block' && a !== 'monetize' && a !== 'track') continue
        const territoryType = r.conditions && r.conditions.requiredTerritories && r.conditions.requiredTerritories.type
        if (territoryType === 'exclude') continue
        seen.add(a)
        out.push(a)
      }
      return out
    },
  },
  watch: {
    '$route.params.id': { immediate: true, handler() { this.load() } },
  },
  methods: {
    async load() {
      const id = this.$route.params.id
      if (!id) return
      this.loading = true
      try {
        const [claim, hist] = await Promise.all([
          YoutubeCidApi.getClaim(id),
          YoutubeCidApi.getClaimHistory(id).catch(() => null),
        ])
        this.claim = claim
        this.history = (hist && hist.events) || []
        if (claim && claim.assetId) {
          const asset = await YoutubeCidApi.getAsset(claim.assetId).catch(() => null)
          this.assetTitle = asset && (asset.metadata || asset.metadataMine || {}).title || ''
        }
      } finally {
        this.loading = false
      }
    },
    formatTime(t) {
      if (!t) return '—'
      return toLocalDateTime(t) || t
    },
    async onRelease() {
      if (!this.claim) return
      this.submitting = true
      try {
        await YoutubeCidApi.updateClaim(this.claim.id, { status: 'inactive' })
        this.releaseDialogOpen = false
        await this.load()
      } finally {
        this.submitting = false
      }
    },
    async openPolicyDialog() {
      if (!this.claim) return
      this.selectedPolicyId = ''
      this.proposedPolicy = null
      this.policyDialogOpen = true
      const applied = this.claim.appliedPolicy || {}
      this.currentPolicyName = applied.name || ''
      this.currentPolicyRules = Array.isArray(applied.rules) ? applied.rules : []
      this.loadingPolicies = true
      try {
        const tasks = [YoutubeCidApi.listPolicies().catch(() => null)]
        if (applied.id) tasks.push(YoutubeCidApi.getPolicy(applied.id).catch(() => null))
        const [list, current] = await Promise.all(tasks)
        this.policies = (list && list.items) || []
        if (current) {
          if (Array.isArray(current.rules) && current.rules.length) this.currentPolicyRules = current.rules
          if (current.name) this.currentPolicyName = current.name
        }
        if (!this.currentPolicyName) {
          const targetKey = policyKey(this.currentPolicyRules)
          if (targetKey) {
            const match = this.policies.find((p) => policyKey(p.rules || []) === targetKey)
            if (match) this.currentPolicyName = match.name || match.id || ''
          }
        }
      } finally {
        this.loadingPolicies = false
      }
    },
    async onPolicyChange(id) {
      if (!id) {
        this.proposedPolicy = null
        return
      }
      const picked = this.policies.find((p) => p.id === id)
      this.proposedPolicy = picked ? { ...picked, rules: picked.rules || [] } : { id, rules: [] }
      const full = await YoutubeCidApi.getPolicy(id).catch(() => null)
      if (full) this.proposedPolicy = full
    },
    async onChangePolicy() {
      if (!this.claim || !this.selectedPolicyId) return
      this.submitting = true
      try {
        await YoutubeCidApi.updateClaim(this.claim.id, { policy: { id: this.selectedPolicyId } })
        this.policyDialogOpen = false
        this.selectedPolicyId = ''
        this.proposedPolicy = null
        await this.load()
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ytcid-back-link:hover { color: #7367F0 !important; }
.ytcid-link:hover { color: #7367F0 !important; }

.policy-swap {
  display: grid;
  grid-template-columns: 1fr 24px 1fr;
  gap: 12px;
  align-items: stretch;
}
.policy-swap__col {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  padding: 14px 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.policy-swap__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}
.policy-swap__name {
  font-size: 14px;
  font-weight: 600;
  color: #0f0f0f;
  word-break: break-word;
}
.policy-swap__name--placeholder {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.4);
}
.policy-swap__loading {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
}
.policy-swap__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.3);
  font-size: 18px;
  font-weight: 400;
}
.policy-swap__picker {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.policy-swap__picker-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
}

@media (max-width: 720px) {
  .policy-swap {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .policy-swap__arrow {
    transform: rotate(90deg);
    padding: 4px 0;
  }
}
</style>
